/**
 * Admin service — API-first.
 *
 * All calls target the Node + Express + MariaDB backend under `/api/admin/*`
 * and require a Bearer token (admin role). When VITE_PUBLIC_API_URL is empty
 * the console runs on demo data so every screen stays explorable in preview.
 *
 * When an API IS configured, request failures PROPAGATE to the caller — admin
 * sections render an honest error + retry state instead of silently showing
 * demo data over a broken backend.
 *
 * Backend endpoint map:
 *   GET    /admin/analytics                 dashboard metrics
 *   GET    /admin/businesses                all listings (filter/search/sort)
 *   PATCH  /admin/businesses/:id/status     set status (active/pending/rejected/suspended)
 *   PATCH  /admin/businesses/:id/flags      toggle featured / sponsored / verified
 *   DELETE /admin/businesses/:id            soft-delete a listing
 *   POST   /admin/businesses/bulk           bulk approve / feature / archive several listings
 *   GET    /admin/categories                categories + subcategories
 *   POST   /admin/categories                create
 *   PATCH  /admin/categories/:id            update
 *   DELETE /admin/categories/:id            soft-delete
 *   GET    /admin/detailed-categories       A–Z detailed category index
 *   POST   /admin/detailed-categories       create
 *   PATCH  /admin/detailed-categories/:id   update
 *   DELETE /admin/detailed-categories/:id   soft-delete (deactivate)
 *   GET    /admin/category-aliases          A–Z name → branded page aliases
 *   POST   /admin/category-aliases          create
 *   PATCH  /admin/category-aliases/:id      update
 *   DELETE /admin/category-aliases/:id      delete
 *   GET    /admin/locations                 districts + towns
 *   POST   /admin/locations                 create
 *   PATCH  /admin/locations/:id             update
 *   DELETE /admin/locations/:id             soft-delete
 *   GET    /admin/users                     users / owners
 *   PATCH  /admin/users/:id/status          suspend / activate
 *   DELETE /admin/users/:id                 deactivate
 *   GET    /admin/ads                       advertisements
 *   POST   /admin/ads · PATCH /admin/ads/:id · DELETE /admin/ads/:id
 *   GET    /admin/ad-placements (+ POST/PATCH/DELETE)
 *   GET    /admin/seo  · POST/PATCH/DELETE
 *   GET    /admin/featured (+ POST/PATCH/DELETE)
 *   GET    /admin/spots (+ POST/PATCH/DELETE)
 *   GET    /admin/subscriptions (+ PATCH :id/status)
 *   GET    /admin/settings · PATCH /admin/settings
 */
import {
  apiGetAuth,
  apiPostAuth,
  apiPatchAuth,
  apiDeleteAuth,
  isApiConfigured,
} from "./http";
import {
  demoAdminBusinesses,
  demoAdminCategories,
  demoAdminSubcategories,
  demoAdminLocations,
  demoAdminUsers,
  demoAdminAds,
  demoAdminAdPlacements,
  demoAdminSeo,
  demoAdminFeatured,
  demoAdminSubscriptions,
  demoAdminTouristSpots,
  demoAdminSettings,
  demoAdminMetrics,
  demoAdminContacts,
  demoAdminOrders,
  demoAdminInvoices,
  demoAdminMedia,
} from "@/mocks/admin";
import type {
  AdminBusiness,
  AdminBusinessDetail,
  AdminBusinessStatus,
  AdminCategory,
  AdminCategoryAlias,
  AdminDetailedCategory,
  AdminLocation,
  AdminLocationType,
  AdminUser,
  AdminAd,
  AdminAdPlacement,
  AdminSeoSetting,
  AdminFeatured,
  AdminSubscription,
  AdminPlan,
  AdminPlanFeature,
  AdminPlanFaq,
  AdminPlansBundle,
  AdminTouristSpot,
  AdminSystemSettings,
  AdminDashboardMetrics,
  AdminMediaItem,
  CrmContact,
  ErpOrder,
  ErpOrderStatus,
  ErpInvoice,
  ErpInvoiceStatus,
} from "@/types/admin";
import { categoryIndexPartA } from "@/mocks/categoryIndexPartA";
import { categoryIndexPartB } from "@/mocks/categoryIndexPartB";
import { adminCategoryAliases as demoAdminCategoryAliases } from "@/mocks/categoryAliases";
import { plans as demoPlans, planComparison, planFaqs } from "@/mocks/plans";
import { notifyPublicSettingsChanged } from "./siteSettingsService";

const delay = (ms = 320): Promise<void> => new Promise((r) => setTimeout(r, ms));
const canUseApi = (token: string | null): boolean => isApiConfigured() && !!token;

/** URL-safe slug for the demo fallback. */
function slugify(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

/** Demo A–Z index — flattened from the bundled category-index mocks (preview only). */
function buildDemoDetailedCategories(): AdminDetailedCategory[] {
  const out: AdminDetailedCategory[] = [];
  let id = 1;
  for (const group of [...categoryIndexPartA, ...categoryIndexPartB]) {
    group.categories.forEach((cat, idx) => {
      out.push({
        id: id++,
        letter: group.letter,
        name: cat.name,
        businessCount: cat.count,
        sortOrder: idx + 1,
        isActive: true,
      });
    });
  }
  return out;
}

const demoAdminDetailedCategories = buildDemoDetailedCategories();

/** Generic GET — demo data only when no API is configured; live errors propagate. */
async function getWithFallback<T>(token: string | null, path: string, demo: T): Promise<T> {
  if (canUseApi(token)) {
    return apiGetAuth<T>(path, token as string);
  }
  await delay();
  return demo;
}

/** Generic write — demo no-op only when no API is configured; live errors propagate. */
async function writeWithFallback(
  token: string | null,
  method: "POST" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
): Promise<void> {
  if (canUseApi(token)) {
    if (method === "POST")   await apiPostAuth(path, body ?? {}, token as string);
    if (method === "PATCH")  await apiPatchAuth(path, body ?? {}, token as string);
    if (method === "DELETE") await apiDeleteAuth(path, token as string);
    return;
  }
  await delay(420);
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

/**
 * The live backend returns analytics in its own shape:
 *   { overview, byCategory, byDistrict, leadsByDay, ordersByPlan, recentOrders }
 * while the console expects:
 *   { totals, trend, byCategory, byDistrict, topBusinesses }
 * This adapter normalises either shape so the dashboard never crashes on a
 * missing `totals` and always renders the numbers the API actually provides.
 */
interface BackendAnalyticsOverview {
  totalBusinesses?: number;
  pendingApprovals?: number;
  totalUsers?: number;
  totalLeads?: number;
  unreadLeads?: number;
  pendingReviews?: number;
  totalRevenue?: number;
}

interface BackendAnalyticsCategory {
  name?: string;
  count?: number;
  categoryName?: string;
  businessCount?: number;
}

interface BackendAnalyticsPayload {
  totals?: AdminDashboardMetrics["totals"];
  overview?: BackendAnalyticsOverview;
  trend?: AdminDashboardMetrics["trend"];
  leadsByDay?: { leadDate: string; leadCount: number }[];
  byCategory?: BackendAnalyticsCategory[];
  byDistrict?: BackendAnalyticsCategory[];
  topBusinesses?: AdminDashboardMetrics["topBusinesses"];
}

const EMPTY_TOTALS: AdminDashboardMetrics["totals"] = {
  totalBusinesses: 0, newBusinesses: 0, businessViews: 0, totalLeads: 0, newLeads: 0,
  totalReviews: 0, pendingReviews: 0, totalUsers: 0, newUsers: 0, owners: 0,
  pendingApprovals: 0, adImpressions: 0, adClicks: 0, featuredListings: 0,
  activeSubscriptions: 0, totalRevenue: 0,
};

function normalizeMetrics(raw: BackendAnalyticsPayload | null | undefined): AdminDashboardMetrics {
  const src = raw ?? {};

  // Already in console shape (demo / legacy) — just fill any gaps.
  if (src.totals) {
    return {
      totals:     { ...EMPTY_TOTALS, ...src.totals },
      trend:      src.trend ?? [],
      byCategory: (src.byCategory ?? []).map((c) => ({
        name: c.name ?? c.categoryName ?? "", count: c.count ?? c.businessCount ?? 0,
      })),
      byDistrict: (src.byDistrict ?? []).map((d) => ({
        name: d.name ?? d.categoryName ?? "", count: d.count ?? d.businessCount ?? 0,
      })),
      topBusinesses: src.topBusinesses ?? [],
    };
  }

  // Live backend shape — map `overview` into `totals`.
  const o = src.overview ?? {};
  return {
    totals: {
      ...EMPTY_TOTALS,
      totalBusinesses:  o.totalBusinesses  ?? 0,
      pendingApprovals: o.pendingApprovals ?? 0,
      totalUsers:       o.totalUsers       ?? 0,
      totalLeads:       o.totalLeads       ?? 0,
      newLeads:         o.unreadLeads      ?? 0,
      pendingReviews:   o.pendingReviews   ?? 0,
      totalRevenue:     Number(o.totalRevenue ?? 0),
    },
    trend: (src.leadsByDay ?? []).map((d) => ({
      date: d.leadDate, leads: d.leadCount, views: 0, signups: 0,
    })),
    byCategory: (src.byCategory ?? []).map((c) => ({
      name: c.name ?? c.categoryName ?? "", count: c.count ?? c.businessCount ?? 0,
    })),
    byDistrict: (src.byDistrict ?? []).map((d) => ({
      name: d.name ?? d.categoryName ?? "", count: d.count ?? d.businessCount ?? 0,
    })),
    topBusinesses: src.topBusinesses ?? [],
  };
}

export async function getDashboardMetrics(token: string | null): Promise<AdminDashboardMetrics> {
  if (canUseApi(token)) {
    const raw = await apiGetAuth<BackendAnalyticsPayload>("/admin/analytics", token as string);
    return normalizeMetrics(raw);
  }
  await delay();
  return demoAdminMetrics;
}

// ── Businesses ────────────────────────────────────────────────────────────────

export function getAdminBusinesses(token: string | null): Promise<AdminBusiness[]> {
  return getWithFallback(token, "/admin/businesses?limit=200", demoAdminBusinesses);
}

/** Everything an admin may set when creating a listing from the console. */
export interface CreateAdminBusinessInput {
  name: string;
  categoryId: number;
  subcategoryId?: number | null;
  districtId: number;
  townId?: number | null;
  planId?: number | null;
  ownerId?: number | null;
  status: "pending" | "active";
  tagline?: string;
  description?: string;
  address?: string;
  pinCode?: string;
  phone?: string;
  whatsapp?: string;
  email?: string;
  website?: string;
  videoUrl?: string;
  logoUrl?: string;
  coverUrl?: string;
  gallery?: string[];
}

/**
 * Create a listing from the admin console.
 * Live: POST /admin/businesses → { id, slug }.
 * Demo: synthesises an id/slug so the console still previews the flow.
 */
export async function createAdminBusiness(
  token: string | null,
  payload: CreateAdminBusinessInput,
): Promise<{ id: number; slug: string }> {
  if (canUseApi(token)) {
    return apiPostAuth<{ id: number; slug: string }>("/admin/businesses", payload, token as string);
  }
  await delay(420);
  const slug = slugify(payload.name);
  return { id: Date.now(), slug: slug || `listing-${Date.now()}` };
}

/** Full editable record for one listing — powers the admin edit form. */
export async function getAdminBusinessDetail(
  token: string | null,
  id: number,
): Promise<AdminBusinessDetail> {
  if (canUseApi(token)) {
    return apiGetAuth<AdminBusinessDetail>(`/admin/businesses/${id}`, token as string);
  }
  await delay();
  const row = demoAdminBusinesses.find((b) => b.id === id);
  if (!row) throw new Error("Listing not found.");
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    tagline: null,
    description: null,
    categoryId: null,
    subcategoryId: null,
    districtId: null,
    townId: null,
    planId: null,
    ownerId: null,
    status: row.status,
    phone: null,
    whatsapp: null,
    email: null,
    website: null,
    address: null,
    pinCode: null,
    videoUrl: row.videoUrl,
    logoUrl: null,
    coverUrl: null,
    isFeatured: row.isFeatured,
    isSponsored: row.isSponsored,
    isVerified: row.isVerified,
    views: row.views,
    rating: row.rating,
    reviewCount: row.reviewCount,
    createdAt: row.createdAt,
    categoryName: row.categoryName,
    districtName: row.districtName,
    townName: row.townName,
    ownerName: row.ownerName,
    ownerEmail: row.ownerEmail,
    planSlug: row.planSlug,
    gallery: [],
  };
}

/** Everything an admin may rewrite on an existing listing. */
export interface UpdateAdminBusinessInput {
  name?: string;
  tagline?: string | null;
  description?: string | null;
  categoryId?: number;
  subcategoryId?: number | null;
  districtId?: number;
  townId?: number | null;
  planId?: number | null;
  ownerId?: number | null;
  status?: AdminBusinessStatus;
  phone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  website?: string | null;
  address?: string | null;
  pinCode?: string | null;
  videoUrl?: string | null;
  logoUrl?: string | null;
  coverUrl?: string | null;
  gallery?: string[];
}

/**
 * Rewrite an existing listing from the admin console.
 * Live: PATCH /admin/businesses/:id. Demo: no-op so the console can preview.
 */
export async function updateAdminBusiness(
  token: string | null,
  id: number,
  payload: UpdateAdminBusinessInput,
): Promise<void> {
  if (canUseApi(token)) {
    await apiPatchAuth(`/admin/businesses/${id}`, payload, token as string);
    return;
  }
  await delay(420);
}

export function setBusinessStatus(
  token: string | null,
  id: number,
  status: AdminBusinessStatus,
  reason?: string,
): Promise<void> {
  return writeWithFallback(token, "PATCH", `/admin/businesses/${id}/status`, { status, reason });
}

export function setBusinessFlags(
  token: string | null,
  id: number,
  flags: Partial<Pick<AdminBusiness, "isFeatured" | "isSponsored" | "isVerified">>,
): Promise<void> {
  return writeWithFallback(token, "PATCH", `/admin/businesses/${id}/flags`, flags);
}

export function deleteBusiness(token: string | null, id: number): Promise<void> {
  return writeWithFallback(token, "DELETE", `/admin/businesses/${id}`);
}

/** Bulk-apply one action to many listings at once. */
export type BulkBusinessAction = "approve" | "feature" | "unfeature" | "archive";

export function bulkBusinessAction(
  token: string | null,
  ids: number[],
  action: BulkBusinessAction,
): Promise<void> {
  return writeWithFallback(token, "POST", "/admin/businesses/bulk", { ids, action });
}

// ── Categories & subcategories ────────────────────────────────────────────────

export function getAdminCategories(token: string | null): Promise<AdminCategory[]> {
  return getWithFallback(token, "/admin/categories", demoAdminCategories);
}

export function getAdminSubcategories(token: string | null): Promise<AdminCategory[]> {
  return getWithFallback(token, "/admin/subcategories", demoAdminSubcategories);
}

export type CategoryPayload = Partial<
  Pick<AdminCategory, "name" | "slug" | "icon" | "description" | "parentId" | "sortOrder" | "isActive">
>;

export function saveCategory(
  token: string | null,
  payload: CategoryPayload,
  id?: number,
): Promise<void> {
  return writeWithFallback(token, id ? "PATCH" : "POST", id ? `/admin/categories/${id}` : "/admin/categories", payload);
}

export function deleteCategory(token: string | null, id: number): Promise<void> {
  return writeWithFallback(token, "DELETE", `/admin/categories/${id}`);
}

export function saveSubcategory(
  token: string | null,
  payload: CategoryPayload,
  id?: number,
): Promise<void> {
  return writeWithFallback(token, id ? "PATCH" : "POST", id ? `/admin/subcategories/${id}` : "/admin/subcategories", payload);
}

export function deleteSubcategory(token: string | null, id: number): Promise<void> {
  return writeWithFallback(token, "DELETE", `/admin/subcategories/${id}`);
}

// ── Detailed A–Z categories ───────────────────────────────────────────────────

export function getAdminDetailedCategories(token: string | null): Promise<AdminDetailedCategory[]> {
  return getWithFallback(token, "/admin/detailed-categories", demoAdminDetailedCategories);
}

export type DetailedCategoryPayload = Partial<
  Pick<AdminDetailedCategory, "letter" | "name" | "businessCount" | "sortOrder" | "isActive">
>;

export function saveDetailedCategory(
  token: string | null,
  payload: DetailedCategoryPayload,
  id?: number,
): Promise<void> {
  return writeWithFallback(
    token,
    id ? "PATCH" : "POST",
    id ? `/admin/detailed-categories/${id}` : "/admin/detailed-categories",
    payload,
  );
}

/** Soft-delete — the A–Z index row is deactivated, not physically removed. */
export function deleteDetailedCategory(token: string | null, id: number): Promise<void> {
  return writeWithFallback(token, "DELETE", `/admin/detailed-categories/${id}`);
}

// ── Category aliases (A–Z name → branded page) ────────────────────────────────

export function getAdminCategoryAliases(token: string | null): Promise<AdminCategoryAlias[]> {
  return getWithFallback(token, "/admin/category-aliases", demoAdminCategoryAliases);
}

export type CategoryAliasPayload = Partial<
  Pick<AdminCategoryAlias, "azName" | "categorySlug" | "isActive">
>;

export function saveCategoryAlias(
  token: string | null,
  payload: CategoryAliasPayload,
  id?: number,
): Promise<void> {
  return writeWithFallback(
    token,
    id ? "PATCH" : "POST",
    id ? `/admin/category-aliases/${id}` : "/admin/category-aliases",
    payload,
  );
}

export function deleteCategoryAlias(token: string | null, id: number): Promise<void> {
  return writeWithFallback(token, "DELETE", `/admin/category-aliases/${id}`);
}

// ── Locations ─────────────────────────────────────────────────────────────────

export function getAdminLocations(token: string | null): Promise<AdminLocation[]> {
  return getWithFallback(token, "/admin/locations", demoAdminLocations);
}

export type LocationPayload = Partial<
  Pick<AdminLocation, "name" | "slug" | "type" | "districtName" | "pinCode" | "sortOrder" | "isActive">
>;

export function saveLocation(
  token: string | null,
  payload: LocationPayload,
  id?: number,
): Promise<void> {
  return writeWithFallback(token, id ? "PATCH" : "POST", id ? `/admin/locations/${id}` : "/admin/locations", payload);
}

export function deleteLocation(token: string | null, id: number, type?: AdminLocationType): Promise<void> {
  const query = type ? `?type=${type}` : "";
  return writeWithFallback(token, "DELETE", `/admin/locations/${id}${query}`);
}

// ── Users & owners ────────────────────────────────────────────────────────────

export function getAdminUsers(token: string | null): Promise<AdminUser[]> {
  return getWithFallback(token, "/admin/users?limit=200", demoAdminUsers);
}

export function setUserStatus(
  token: string | null,
  id: number,
  status: AdminUser["status"],
): Promise<void> {
  return writeWithFallback(token, "PATCH", `/admin/users/${id}/status`, { status });
}

export function deactivateUser(token: string | null, id: number): Promise<void> {
  return writeWithFallback(token, "DELETE", `/admin/users/${id}`);
}

// ── Advertisements & placements ───────────────────────────────────────────────

export function getAdminAds(token: string | null): Promise<AdminAd[]> {
  return getWithFallback(token, "/admin/ads", demoAdminAds);
}

export type AdPayload = Partial<
  Pick<AdminAd, "title" | "placementSlug" | "targetUrl" | "imageUrl" | "categoryName" | "status" | "startsAt" | "endsAt">
>;

export function saveAd(token: string | null, payload: AdPayload, id?: number): Promise<void> {
  return writeWithFallback(token, id ? "PATCH" : "POST", id ? `/admin/ads/${id}` : "/admin/ads", payload);
}

export function deleteAd(token: string | null, id: number): Promise<void> {
  return writeWithFallback(token, "DELETE", `/admin/ads/${id}`);
}

export function getAdminAdPlacements(token: string | null): Promise<AdminAdPlacement[]> {
  return getWithFallback(token, "/admin/ad-placements", demoAdminAdPlacements);
}

export type AdPlacementPayload = Partial<
  Pick<AdminAdPlacement, "name" | "slug" | "description" | "width" | "height" | "isActive">
>;

export function saveAdPlacement(
  token: string | null,
  payload: AdPlacementPayload,
  id?: number,
): Promise<void> {
  return writeWithFallback(token, id ? "PATCH" : "POST", id ? `/admin/ad-placements/${id}` : "/admin/ad-placements", payload);
}

export function deleteAdPlacement(token: string | null, id: number): Promise<void> {
  return writeWithFallback(token, "DELETE", `/admin/ad-placements/${id}`);
}

// ── SEO settings ──────────────────────────────────────────────────────────────

export function getAdminSeo(token: string | null): Promise<AdminSeoSetting[]> {
  return getWithFallback(token, "/admin/seo", demoAdminSeo);
}

export type SeoPayload = Partial<
  Pick<AdminSeoSetting, "route" | "pageLabel" | "title" | "description" | "keywords" | "ogTitle" | "ogDescription" | "noindex">
>;

export function saveSeo(token: string | null, payload: SeoPayload, id?: number): Promise<void> {
  return writeWithFallback(token, id ? "PATCH" : "POST", id ? `/admin/seo/${id}` : "/admin/seo", payload);
}

export function deleteSeo(token: string | null, id: number): Promise<void> {
  return writeWithFallback(token, "DELETE", `/admin/seo/${id}`);
}

// ── Featured listings ─────────────────────────────────────────────────────────

export function getAdminFeatured(token: string | null): Promise<AdminFeatured[]> {
  return getWithFallback(token, "/admin/featured", demoAdminFeatured);
}

export type FeaturedPayload = Partial<
  Pick<AdminFeatured, "slug" | "position" | "isActive" | "startsAt" | "endsAt">
>;

export function saveFeatured(token: string | null, payload: FeaturedPayload, id?: number): Promise<void> {
  return writeWithFallback(token, id ? "PATCH" : "POST", id ? `/admin/featured/${id}` : "/admin/featured", payload);
}

export function deleteFeatured(token: string | null, id: number): Promise<void> {
  return writeWithFallback(token, "DELETE", `/admin/featured/${id}`);
}

// ── Tourist spots ─────────────────────────────────────────────────────────────

export function getAdminTouristSpots(token: string | null): Promise<AdminTouristSpot[]> {
  return getWithFallback(token, "/admin/spots", demoAdminTouristSpots);
}

export type TouristSpotPayload = Partial<
  Pick<
    AdminTouristSpot,
    | "name"
    | "slug"
    | "districtName"
    | "category"
    | "blurb"
    | "description"
    | "highlights"
    | "image"
    | "timings"
    | "entryFee"
    | "bestTime"
    | "idealDuration"
    | "address"
    | "featured"
    | "status"
    | "sortOrder"
  >
>;

export function saveTouristSpot(
  token: string | null,
  payload: TouristSpotPayload,
  id?: number,
): Promise<void> {
  return writeWithFallback(token, id ? "PATCH" : "POST", id ? `/admin/spots/${id}` : "/admin/spots", payload);
}

export function deleteTouristSpot(token: string | null, id: number): Promise<void> {
  return writeWithFallback(token, "DELETE", `/admin/spots/${id}`);
}

// ── Subscriptions ─────────────────────────────────────────────────────────────

export function getAdminSubscriptions(token: string | null): Promise<AdminSubscription[]> {
  return getWithFallback(token, "/admin/subscriptions", demoAdminSubscriptions);
}

export function setSubscriptionStatus(
  token: string | null,
  id: number,
  status: AdminSubscription["status"],
): Promise<void> {
  return writeWithFallback(token, "PATCH", `/admin/subscriptions/${id}/status`, { status });
}

// ── Listing plans (pricing page) ──────────────────────────────────────────────

/** Demo plans bundle — built from the bundled pricing mocks (preview only). */
const demoAdminPlansBundle: AdminPlansBundle = {
  plans: demoPlans.map((p, i) => ({
    id: i + 1,
    slug: p.slug,
    name: p.name,
    tagline: p.tagline,
    priceYearly: p.priceYearly,
    durationDays: p.priceYearly === 0 ? 0 : 365,
    icon: p.icon,
    isPopular: p.popular,
    isActive: true,
    displayOrder: i + 1,
    highlights: [...p.highlights],
  })),
  features: planComparison.map((r, i) => ({
    id: i + 1,
    feature: r.feature,
    freeValue: r.free,
    businessValue: r.business,
    premiumValue: r.premium,
    rowOrder: i + 1,
  })),
  faqs: planFaqs.map((f, i) => ({
    id: i + 1,
    question: f.question,
    answer: f.answer,
    sort: i + 1,
    isActive: true,
  })),
};

export function getAdminPlans(token: string | null): Promise<AdminPlansBundle> {
  return getWithFallback(token, "/admin/plans", demoAdminPlansBundle);
}

export type PlanPayload = Partial<Omit<AdminPlan, "id">>;

export function saveAdminPlan(
  token: string | null,
  payload: PlanPayload,
  id?: number,
): Promise<void> {
  return writeWithFallback(token, id ? "PATCH" : "POST", id ? `/admin/plans/${id}` : "/admin/plans", payload);
}

export function deleteAdminPlan(token: string | null, id: number): Promise<void> {
  return writeWithFallback(token, "DELETE", `/admin/plans/${id}`);
}

export type PlanFeaturePayload = Partial<Omit<AdminPlanFeature, "id">>;

export function saveAdminPlanFeature(
  token: string | null,
  payload: PlanFeaturePayload,
  id?: number,
): Promise<void> {
  return writeWithFallback(
    token,
    id ? "PATCH" : "POST",
    id ? `/admin/plan-features/${id}` : "/admin/plan-features",
    payload,
  );
}

export function deleteAdminPlanFeature(token: string | null, id: number): Promise<void> {
  return writeWithFallback(token, "DELETE", `/admin/plan-features/${id}`);
}

export type PlanFaqPayload = Partial<Omit<AdminPlanFaq, "id">>;

export function saveAdminPlanFaq(
  token: string | null,
  payload: PlanFaqPayload,
  id?: number,
): Promise<void> {
  return writeWithFallback(
    token,
    id ? "PATCH" : "POST",
    id ? `/admin/plan-faqs/${id}` : "/admin/plan-faqs",
    payload,
  );
}

export function deleteAdminPlanFaq(token: string | null, id: number): Promise<void> {
  return writeWithFallback(token, "DELETE", `/admin/plan-faqs/${id}`);
}

// ── System settings ───────────────────────────────────────────────────────────

export function getAdminSettings(token: string | null): Promise<AdminSystemSettings> {
  return getWithFallback(token, "/admin/settings", demoAdminSettings);
}

export function saveAdminSettings(
  token: string | null,
  payload: Partial<AdminSystemSettings>,
): Promise<void> {
  return writeWithFallback(token, "PATCH", "/admin/settings", payload).then(() => {
    // Apply branding / maintenance changes across every open tab immediately.
    notifyPublicSettingsChanged();
  });
}

// ── Media library ─────────────────────────────────────────────────────────────

export function getAdminMedia(token: string | null, folder?: string): Promise<AdminMediaItem[]> {
  const query = folder ? `?folder=${encodeURIComponent(folder)}` : "";
  return getWithFallback(token, `/admin/media${query}`, demoAdminMedia);
}

export function deleteAdminMedia(
  token: string | null,
  folder: string,
  name: string,
): Promise<void> {
  const query = `?folder=${encodeURIComponent(folder)}&name=${encodeURIComponent(name)}`;
  return writeWithFallback(token, "DELETE", `/admin/media${query}`);
}

// ── CRM & ERP input contracts ─────────────────────────────────────────────────

export interface CreateOrderInput {
  customer: string;
  customerEmail?: string | null;
  business?: string;
  plan?: string;
  amount?: number;
  currency?: string;
  paymentMethod?: string;
  status?: ErpOrderStatus;
}

export type OrderUpdateInput = Partial<CreateOrderInput>;

export interface CreateInvoiceInput {
  customer: string;
  customerEmail?: string | null;
  business?: string;
  plan?: string;
  amount: number;
  tax?: number;
  total?: number;
  currency?: string;
  status?: ErpInvoiceStatus;
  dueAt?: string;
}

export type InvoiceUpdateInput = Partial<CreateInvoiceInput>;

export interface CrmErpCounters {
  openDeals: number;
  unpaidInvoices: number;
}

export interface ContactActivity {
  orders: ErpOrder[];
  invoices: ErpInvoice[];
}

// ── CRM contacts (persisted in the MariaDB backend) ───────────────────────────

export type ContactPayload = Partial<
  Pick<CrmContact, "name" | "company" | "email" | "phone" | "stage" | "value" | "owner" | "source">
>;

export async function getAdminContacts(token: string | null): Promise<CrmContact[]> {
  if (canUseApi(token)) return apiGetAuth<CrmContact[]>("/admin/contacts", token as string);
  await delay();
  return demoAdminContacts;
}

export async function saveContact(
  token: string | null,
  payload: ContactPayload,
  id?: number,
): Promise<void> {
  if (canUseApi(token)) {
    if (id) await apiPatchAuth(`/admin/contacts/${id}`, payload, token as string);
    else await apiPostAuth("/admin/contacts", payload, token as string);
    return;
  }
  await delay(420);
}

export async function deleteContact(token: string | null, id: number): Promise<void> {
  if (canUseApi(token)) {
    await apiDeleteAuth(`/admin/contacts/${id}`, token as string);
    return;
  }
  await delay(420);
}

/** Orders + invoices tied to a contact — powers the CRM detail drawer. */
export async function getContactActivity(
  token: string | null,
  contact: CrmContact,
): Promise<ContactActivity> {
  if (canUseApi(token)) {
    return apiGetAuth<ContactActivity>(`/admin/contacts/${contact.id}/activity`, token as string);
  }
  await delay(320);
  return { orders: [], invoices: [] };
}

// ── ERP orders (persisted in the MariaDB backend) ─────────────────────────────

export async function getAdminOrders(token: string | null): Promise<ErpOrder[]> {
  if (canUseApi(token)) return apiGetAuth<ErpOrder[]>("/admin/orders", token as string);
  await delay();
  return demoAdminOrders;
}

export async function setOrderStatus(
  token: string | null,
  id: number,
  status: ErpOrderStatus,
): Promise<void> {
  if (canUseApi(token)) {
    await apiPatchAuth(`/admin/orders/${id}/status`, { status }, token as string);
    return;
  }
  await delay(420);
}

/** Edit an existing listing-plan order (admin row action). */
export async function editAdminOrder(
  token: string | null,
  id: number,
  payload: OrderUpdateInput,
): Promise<ErpOrder> {
  if (canUseApi(token)) return apiPatchAuth<ErpOrder>(`/admin/orders/${id}`, payload, token as string);
  await delay(420);
  return {
    id,
    orderNo: `ORD-DEMO-${id}`,
    customer: payload.customer ?? "Demo customer",
    customerEmail: payload.customerEmail ?? null,
    business: payload.business ?? "",
    plan: payload.plan ?? "",
    amount: payload.amount ?? 0,
    currency: payload.currency ?? "INR",
    paymentMethod: payload.paymentMethod ?? "Manual entry",
    status: payload.status ?? "pending",
    invoiceId: null,
    invoiceNo: null,
    createdAt: new Date().toISOString(),
  };
}

/** Delete an order (admin row action). */
export async function deleteAdminOrder(token: string | null, id: number): Promise<void> {
  if (canUseApi(token)) {
    await apiDeleteAuth(`/admin/orders/${id}`, token as string);
    return;
  }
  await delay(420);
}

/** Raise an invoice straight from an order and link the two records. */
export async function generateInvoiceFromOrder(
  token: string | null,
  order: ErpOrder,
): Promise<ErpInvoice> {
  if (canUseApi(token)) return apiPostAuth<ErpInvoice>(`/admin/orders/${order.id}/invoice`, {}, token as string);
  await delay(420);
  const amount = order.amount;
  const tax = Math.round(amount * 0.18);
  return {
    id: Date.now(),
    invoiceNo: `INV-DEMO-${Math.floor(1000 + Math.random() * 9000)}`,
    customer: order.customer,
    customerEmail: order.customerEmail,
    business: order.business,
    plan: order.plan,
    amount,
    tax,
    total: amount + tax,
    currency: order.currency,
    status: "draft",
    orderId: order.id,
    orderNo: order.orderNo,
    issuedAt: new Date().toISOString(),
    dueAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  };
}

/** Manually create a listing-plan order from the admin console. */
export async function createAdminOrder(
  token: string | null,
  payload: CreateOrderInput,
): Promise<ErpOrder> {
  if (canUseApi(token)) return apiPostAuth<ErpOrder>("/admin/orders", payload, token as string);
  await delay(420);
  return {
    id: Date.now(),
    orderNo: `ORD-DEMO-${Math.floor(1000 + Math.random() * 9000)}`,
    customer: payload.customer,
    customerEmail: payload.customerEmail ?? null,
    business: payload.business ?? "",
    plan: payload.plan ?? "",
    amount: payload.amount ?? 0,
    currency: payload.currency ?? "INR",
    paymentMethod: payload.paymentMethod ?? "Manual entry",
    status: payload.status ?? "pending",
    invoiceId: null,
    invoiceNo: null,
    createdAt: new Date().toISOString(),
  };
}

// ── ERP invoices (persisted in the MariaDB backend) ───────────────────────────

export async function getAdminInvoices(token: string | null): Promise<ErpInvoice[]> {
  if (canUseApi(token)) return apiGetAuth<ErpInvoice[]>("/admin/invoices", token as string);
  await delay();
  return demoAdminInvoices;
}

export async function setInvoiceStatus(
  token: string | null,
  id: number,
  status: ErpInvoiceStatus,
): Promise<void> {
  if (canUseApi(token)) {
    await apiPatchAuth(`/admin/invoices/${id}/status`, { status }, token as string);
    return;
  }
  await delay(420);
}

/** Edit an existing invoice (admin row action). */
export async function editAdminInvoice(
  token: string | null,
  id: number,
  payload: InvoiceUpdateInput,
): Promise<ErpInvoice> {
  if (canUseApi(token)) return apiPatchAuth<ErpInvoice>(`/admin/invoices/${id}`, payload, token as string);
  await delay(420);
  const amount = payload.amount ?? 0;
  const tax = payload.tax ?? Math.round(amount * 0.18);
  return {
    id,
    invoiceNo: `INV-DEMO-${id}`,
    customer: payload.customer ?? "Demo customer",
    customerEmail: payload.customerEmail ?? null,
    business: payload.business ?? "",
    plan: payload.plan ?? "",
    amount,
    tax,
    total: payload.total ?? amount + tax,
    currency: payload.currency ?? "INR",
    status: payload.status ?? "draft",
    orderId: null,
    orderNo: null,
    issuedAt: new Date().toISOString(),
    dueAt: payload.dueAt ?? new Date().toISOString(),
  };
}

/** Delete an invoice (admin row action). */
export async function deleteAdminInvoice(token: string | null, id: number): Promise<void> {
  if (canUseApi(token)) {
    await apiDeleteAuth(`/admin/invoices/${id}`, token as string);
    return;
  }
  await delay(420);
}

/** Manually raise an invoice from the admin console. */
export async function createAdminInvoice(
  token: string | null,
  payload: CreateInvoiceInput,
): Promise<ErpInvoice> {
  if (canUseApi(token)) return apiPostAuth<ErpInvoice>("/admin/invoices", payload, token as string);
  await delay(420);
  const amount = payload.amount ?? 0;
  const tax = payload.tax ?? Math.round(amount * 0.18);
  return {
    id: Date.now(),
    invoiceNo: `INV-DEMO-${Math.floor(1000 + Math.random() * 9000)}`,
    customer: payload.customer,
    customerEmail: payload.customerEmail ?? null,
    business: payload.business ?? "",
    plan: payload.plan ?? "",
    amount,
    tax,
    total: payload.total ?? amount + tax,
    currency: payload.currency ?? "INR",
    status: payload.status ?? "draft",
    orderId: null,
    orderNo: null,
    issuedAt: new Date().toISOString(),
    dueAt: payload.dueAt ?? new Date().toISOString(),
  };
}

// ── Live sidebar counters ─────────────────────────────────────────────────────

/** Open CRM deals + unpaid invoices, used for the admin sidebar badges. */
export async function getAdminCounters(token: string | null): Promise<CrmErpCounters> {
  if (canUseApi(token)) return apiGetAuth<CrmErpCounters>("/admin/counters", token as string);
  await delay(200);
  const openDeals = demoAdminContacts.filter((c) => c.stage !== "won" && c.stage !== "lost").length;
  const unpaidInvoices = demoAdminInvoices.filter((i) => i.status === "sent" || i.status === "overdue").length;
  return { openDeals, unpaidInvoices };
}

/** Owner portal — the signed-in user's own listing-plan orders. */
export async function getMyOrders(token: string | null): Promise<ErpOrder[]> {
  if (canUseApi(token)) return apiGetAuth<ErpOrder[]>("/erp/my-orders", token as string);
  await delay();
  return [];
}

export type { AdminLocationType };